import {Component, inject, ChangeDetectionStrategy, computed} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {IdentityService} from "./core/services/identity.service";
import {NavigationEnd, Router, RouterOutlet} from "@angular/router";
import {ToastModule} from "primeng/toast";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {filter, map} from "rxjs";
import {ShellComponent} from "./core/shell/shell.component";

/** Адрес страницы входа (app.routes.ts, path 'login'); query и fragment не учитываются. */
function isLoginUrl(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0];
  return path === '/login' || path.startsWith('/login/');
}

// Классы целиком литералами: Tailwind собирает утилиты по тексту исходников (@source в tailwind.css).
const TOAST_ICONS: Record<string, string> = {
  success: 'pi-check',
  info: 'pi-info-circle',
  error: 'pi-times-circle',
  warn: 'pi-exclamation-triangle',
  secondary: 'pi-info-circle',
  contrast: 'pi-info-circle',
};

const TOAST_DETAIL_COLORS: Record<string, string> = {
  normal: 'text-(color:--p-toast-normal-detail-color)',
  success: 'text-(color:--p-toast-success-detail-color)',
  info: 'text-(color:--p-toast-info-detail-color)',
  error: 'text-(color:--p-toast-error-detail-color)',
  warn: 'text-(color:--p-toast-warn-detail-color)',
  secondary: 'text-(color:--p-toast-secondary-detail-color)',
  contrast: 'text-(color:--p-toast-contrast-detail-color)',
};

@Component({
  selector: 'app-root',
  imports: [ConfirmDialogModule, RouterOutlet, ToastModule, ShellComponent],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  private identityService = inject(IdentityService);
  private router = inject(Router);
  // Меню, крошка и блок пользователя — в core/shell (PAR-12961); модель меню с видимостью по ролям — core/shell/navigation.ts.
  public isAuthenticated = toSignal(this.identityService.isAuth, {requireSync: true});

  // Адрес завершённой навигации. isAuth становится true раньше, чем страница входа уходит на '/': без этой проверки
  // карточка входа на мгновение оказывалась в оболочке (у пользователя без доступа — ещё и с пустым меню до logout).
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    {initialValue: this.router.url},
  );

  /** Меню и верхняя полоса: пользователь вошёл и текущая страница — не вход. */
  public readonly chrome = computed(() => this.isAuthenticated() && !isLoginUrl(this.url()));

  /** Значок toast по severity — те же значки, что у штатной разметки PrimeNG (ToastItem.severityIcons); без severity — нет. */
  public toastIcon(severity: string | undefined): string {
    return (severity && TOAST_ICONS[severity]) || 'hidden';
  }

  /** Цвет detail toast — токен Aura своей severity (у штатной разметки — правило темы по классу severity плашки). */
  public toastDetailColor(severity: string | undefined): string {
    return (severity && TOAST_DETAIL_COLORS[severity]) || TOAST_DETAIL_COLORS['normal'];
  }
}
